import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";
import { CheckIcon, ArrowRightIcon } from "lucide-react";

const steps = [
  {
    id: 1,
    label: "Personal Info",
    type: "multi",
    fields: [
      { field: "fullName", label: "Name", placeholder: "Your full name", inputType: "text" },
      { field: "email", label: "Email", placeholder: "you@example.com", inputType: "email" },
    ],
  },
  {
    id: 2,
    label: "School",
    field: "school",
    placeholder: "Your school or university",
    type: "text",
  },
  {
    id: 3,
    label: "Year",
    field: "year",
    placeholder: "Select your year",
    type: "select",
    options: [
      { value: "freshman", label: "Freshman" },
      { value: "sophomore", label: "Sophomore" },
      { value: "junior", label: "Junior" },
      { value: "senior", label: "Senior" },
    ],
  },
  {
    id: 4,
    label: "How did you hear about us?",
    field: "referralSource",
    type: "choice",
    options: [
      { value: "friends", label: "Friends" },
      { value: "social-media", label: "Social Media" },
      { value: "student-org", label: "Student Organization" },
      { value: "other", label: "Other" },
    ],
  },
];

const animationStyles = `
  @keyframes onb-fade-up {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes onb-fade-up-sm {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes onb-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes onb-zoom-in {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes onb-zoom-check {
    from { opacity: 0; transform: scale(0.5); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes onb-slide-up {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .onb-fade-up {
    animation: onb-fade-up 1s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
    opacity: 0;
  }
  .onb-fade-up-d1 {
    animation: onb-fade-up-sm 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards !important;
    opacity: 0;
  }
  .onb-fade-up-d2 {
    animation: onb-fade-up-sm 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards !important;
    opacity: 0;
  }
  .onb-fade-d3 {
    animation: onb-fade-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards !important;
    opacity: 0;
  }
  .onb-fade-up-d4 {
    animation: onb-fade-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards !important;
    opacity: 0;
  }
  .onb-step-enter {
    animation: onb-slide-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
  }
  .onb-complete-enter {
    animation: onb-zoom-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
    opacity: 0;
  }
  .onb-check-enter {
    animation: onb-zoom-check 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards !important;
    opacity: 0;
  }
  .onb-check-sm {
    animation: onb-zoom-check 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards !important;
  }
`;

function OnboardingFlow({ onComplete, userEmail, initialData = {} }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    fullName: initialData.fullName || "",
    email: userEmail || initialData.email || "",
    school: initialData.school || "",
    year: initialData.year || "",
    referralSource: initialData.referralSource || "",
    referralOther: initialData.referralOther || "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompleteOnboarding = async () => {
    setLoading(true);
    setError("");

    try {
      const profileData = {
        fullName: formData.fullName,
        email: formData.email,
        school: formData.school,
        year: formData.year,
        referralSource: formData.referralSource,
        referralOther: formData.referralOther,
      };

      setTimeout(() => {
        onComplete(profileData);
      }, 1000);
    } catch (err) {
      console.error("Error completing onboarding:", err);
      setError("Error completing onboarding. Please try again.");
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleCompleteOnboarding();
    }
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const isStepValid = (() => {
    if (currentStepData.type === "multi") {
      return currentStepData.fields.every(
        (f) => (formData[f.field] || "").trim().length > 0
      );
    }
    if (currentStepData.type === "choice") {
      const val = formData[currentStepData.field] || "";
      if (val === "other") return (formData.referralOther || "").trim().length > 0;
      return val.length > 0;
    }
    return (formData[currentStepData.field] || "").trim().length > 0;
  })();

  if (loading) {
    return (
      <>
        <style>{animationStyles}</style>
        <div
          className="onboarding-flow min-h-screen flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, #000000 0%, #111111 100%)",
          }}
        >
          <div className="w-full max-w-sm mx-4">
            <div className="onb-complete-enter relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-black via-black to-white/5 p-12 backdrop-blur">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),transparent_50%)]" />
              <div className="relative flex flex-col items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/10 bg-white/5">
                  <CheckIcon
                    className="h-8 w-8 text-white onb-check-enter"
                    strokeWidth={2.5}
                  />
                </div>
                <div className="space-y-1 text-center">
                  <h2 className="text-xl font-medium tracking-tight text-white text-balance">
                    You're all set
                  </h2>
                  <p className="text-sm text-white/40">{formData.fullName}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{animationStyles}</style>
      <div
        className="onboarding-flow min-h-screen flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, #000000 0%, #111111 100%)",
        }}
      >
        <div className="w-full max-w-sm mx-4">
          {/* Welcome Header */}
          <div className="text-center mb-12 onb-fade-up">
            <h1
              className="text-[2rem] font-semibold tracking-tight text-white leading-tight"
              style={{ fontFeatureSettings: "'ss01', 'ss02'" }}
            >
              Welcome
            </h1>
            <p className="mt-2 text-[0.9375rem] text-white/40 font-normal tracking-normal onb-fade-up-d1">
              A few questions before you're ready
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 px-4 py-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm onb-step-enter">
              <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white text-xs font-bold">!</span>
              </div>
              <div className="flex-1">{error}</div>
            </div>
          )}

          {/* Step Indicators */}
          <div className="mb-10 flex items-center justify-center gap-3 onb-fade-up-d2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center gap-3">
                <button
                  onClick={() => index < currentStep && setCurrentStep(index)}
                  disabled={index > currentStep}
                  className={cn(
                    "group relative flex h-9 w-9 items-center justify-center rounded-full transition-all duration-700 ease-out",
                    "disabled:cursor-not-allowed",
                    index < currentStep && "bg-white/10 text-white/60",
                    index === currentStep &&
                      "bg-white text-black shadow-[0_0_20px_-5px_rgba(255,255,255,0.3)]",
                    index > currentStep && "bg-white/5 text-white/25"
                  )}
                >
                  {index < currentStep ? (
                    <CheckIcon
                      className="h-4 w-4 onb-check-sm"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <span className="text-sm font-medium tabular-nums">
                      {step.id}
                    </span>
                  )}
                  {index === currentStep && (
                    <div className="absolute inset-0 rounded-full bg-white/20 blur-md animate-pulse" />
                  )}
                </button>
                {index < steps.length - 1 && (
                  <div className="relative h-[1.5px] w-12">
                    <div className="absolute inset-0 bg-[rgba(207,207,207,0.15)]" />
                    <div
                      className="absolute inset-0 bg-white/30 transition-all duration-700 ease-out origin-left"
                      style={{
                        transform: `scaleX(${index < currentStep ? 1 : 0})`,
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mb-8 overflow-hidden rounded-full bg-white/5 h-[2px] onb-fade-d3">
            <div
              className="h-full bg-gradient-to-r from-white/40 to-white transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Step Content */}
          <div className="space-y-8 onb-fade-up-d4">
            <div
              key={currentStepData.id}
              className="space-y-4 onb-step-enter"
            >
              <div className="flex items-baseline justify-between">
                <Label className="text-lg font-medium tracking-tight text-white">
                  {currentStepData.label}
                </Label>
                <span className="text-xs font-medium text-white/30 tabular-nums">
                  {currentStep + 1}/{steps.length}
                </span>
              </div>

              {/* Multi-field step (Personal Info) */}
              {currentStepData.type === "multi" && (
                <div className="space-y-4">
                  {currentStepData.fields.map((f, i) => (
                    <div key={f.field} className="relative group">
                      <Label
                        htmlFor={f.field}
                        className="text-xs font-medium text-white/40 mb-1.5 block"
                      >
                        {f.label}
                      </Label>
                      <Input
                        id={f.field}
                        type={f.inputType || "text"}
                        placeholder={f.placeholder}
                        value={formData[f.field] || ""}
                        onChange={(e) =>
                          handleInputChange(f.field, e.target.value)
                        }
                        autoFocus={i === 0}
                        className="h-14 text-base transition-all duration-500 border-white/10 focus:border-white/20 bg-black/50 backdrop-blur text-white placeholder:text-white/30"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Single text input */}
              {currentStepData.type === "text" && (
                <div className="relative group">
                  <Input
                    id={currentStepData.field}
                    type="text"
                    placeholder={currentStepData.placeholder}
                    value={formData[currentStepData.field] || ""}
                    onChange={(e) =>
                      handleInputChange(currentStepData.field, e.target.value)
                    }
                    autoFocus
                    className="h-14 text-base transition-all duration-500 border-white/10 focus:border-white/20 bg-black/50 backdrop-blur text-white placeholder:text-white/30"
                  />
                </div>
              )}

              {/* Select dropdown */}
              {currentStepData.type === "select" && (
                <div className="relative group">
                  <select
                    id={currentStepData.field}
                    value={formData[currentStepData.field] || ""}
                    onChange={(e) =>
                      handleInputChange(currentStepData.field, e.target.value)
                    }
                    autoFocus
                    className="flex h-14 w-full rounded-md border border-white/10 bg-black/50 px-3 py-2 text-base text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:cursor-not-allowed disabled:opacity-50 appearance-none backdrop-blur transition-all duration-500 focus:border-white/20"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                    }}
                  >
                    <option value="" disabled className="bg-[#0a0a0a] text-white/40">
                      {currentStepData.placeholder}
                    </option>
                    {currentStepData.options.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        className="bg-[#0a0a0a] text-white"
                      >
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Choice buttons (referral source) */}
              {currentStepData.type === "choice" && (
                <div className="space-y-3">
                  {currentStepData.options.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        handleInputChange(currentStepData.field, opt.value)
                      }
                      className={cn(
                        "w-full text-left px-4 py-3.5 rounded-lg border text-sm font-medium transition-all duration-300",
                        formData[currentStepData.field] === opt.value
                          ? "border-white/30 bg-white/10 text-white"
                          : "border-white/10 bg-black/30 text-white/60 hover:border-white/20 hover:text-white/80"
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                  {formData[currentStepData.field] === "other" && (
                    <Input
                      type="text"
                      placeholder="Tell us more..."
                      value={formData.referralOther || ""}
                      onChange={(e) =>
                        handleInputChange("referralOther", e.target.value)
                      }
                      autoFocus
                      className="h-12 text-sm transition-all duration-500 border-white/10 focus:border-white/20 bg-black/50 backdrop-blur text-white placeholder:text-white/30 mt-1"
                    />
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={handleNext}
              disabled={!isStepValid || loading}
              className="w-full h-12 group relative transition-all duration-300 hover:shadow-lg hover:shadow-white/5 bg-white text-black hover:bg-white/90 disabled:opacity-30 disabled:bg-white/10 disabled:text-white/30"
            >
              <span className="flex items-center justify-center gap-2 font-medium">
                {currentStep === steps.length - 1 ? "Complete" : "Continue"}
                <ArrowRightIcon
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5 duration-300"
                  strokeWidth={2}
                />
              </span>
            </Button>

            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="w-full text-center text-sm text-white/30 hover:text-white/60 transition-all duration-300"
              >
                Go back
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default OnboardingFlow;
